import {
    bexHttpClient
} from '../../clients';
import {
    CreateApiRequest,
    UpdateApiRequest,
    DeleteApiRequest,
    CreateApiResponse,
    UpdateApiResponse,
    DeleteApiResponse,
    ListApisResponse,
} from '../bexService.types';

export type ApiSecretField = 'token' | 'password' | 'value';

export interface WriteApiSecretRequest {
    organizationId: string;
    field: ApiSecretField | `header:${string}`;
    value: string;
}

export interface WriteApiSecretResponse {
    ref: string;
    headerName?: string;
}

const API_SECRET_FIELDS: ReadonlyArray<ApiSecretField> = [
    'token',
    'password',
    'value',
];

/** Header names whose values are always vaulted (case-insensitive). */
export const SENSITIVE_HEADER_NAMES = new Set([
    'authorization',
    'proxy-authorization',
    'x-api-key',
    'api-key',
    'apikey',
    'x-auth-token',
    'x-access-token',
    'x-api-token',
    'authentication',
]);

export function isSensitiveHeaderName(name: string): boolean {
    return SENSITIVE_HEADER_NAMES.has(String(name || '').trim().toLowerCase());
}

export function looksLikeAuthHeaderValue(value: string): boolean {
    return /^(bearer|basic)\s+\S+/i.test(String(value || '').trim());
}

export function isSensitiveHeader(name: string, value?: string): boolean {
    return isSensitiveHeaderName(name) || looksLikeAuthHeaderValue(value || '');
}

/**
 * Service class for managing user api manager.
 *
 * Save flow (mirrors MCP):
 *   (1) create with auth.type=none to obtain apiId (create path only)
 *   (2) write auth secrets to Vault; sensitive headers are peeled server-side
 *   (3) update the API doc with auth.*_ref + secret_headers (never plaintext)
 */
class ApiManagerService {

    async createNewApi(data: CreateApiRequest): Promise<CreateApiResponse> {
        const response = await bexHttpClient.post('/redis/api-manager/add-api', data);
        return response;
    }

    async updateApi(data: UpdateApiRequest): Promise<UpdateApiResponse> {
        const response = await bexHttpClient.put('/redis/api-manager/update-api', data);
        return response;
    }

    async listApis(organizationId: string, subOrganizationId: string): Promise<ListApisResponse> {
        const response = await bexHttpClient.get(`redis/api-manager/list-apis?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`);
        return response;
    }

    async deleteApi(data: DeleteApiRequest): Promise<DeleteApiResponse> {
        const response = await bexHttpClient.delete('/redis/api-manager/delete-api', {
            data
        });
        return response;
    }

    async writeApiSecret(
        apiId: string,
        body: WriteApiSecretRequest,
    ): Promise<WriteApiSecretResponse> {
        const field = body.field;
        const allowedAuth = API_SECRET_FIELDS.includes(field as ApiSecretField);
        const allowedHeader = typeof field === 'string' && field.startsWith('header:');
        if (!allowedAuth && !allowedHeader) {
            throw new Error(
                `API secret field '${field}' not allowed. Must be one of: ${API_SECRET_FIELDS.join(', ')}, or header:<Name>`,
            );
        }
        const response = await bexHttpClient.post<WriteApiSecretResponse>(
            `/redis/api-manager/${apiId}/secret`,
            body,
        );
        return response.data;
    }

    /**
     * Persist an API with Vault-backed auth + sensitive headers.
     *
     * `secrets` — auth write-only values (token/password/value)
     * `headerSecrets` — sensitive header write-only values (name -> new value)
     * `keepSecretHeaderNames` — secret header names still present in the form (keep refs)
     */
    async persistApiWithVaultAuth(
        data: CreateApiRequest & {
            apiId?: string;
            secret_headers_configured?: string[];
        },
        secrets: Partial<Record<ApiSecretField, string>> = {},
        headerSecrets: Record<string, string> = {},
        keepSecretHeaderNames: string[] = [],
    ): Promise<CreateApiResponse | UpdateApiResponse> {
        const authType = data.auth?.type || 'none';
        const isUpdate = Boolean(data.apiId);
        let apiId = data.apiId;

        // Strip sensitive header plaintext from the public headers map; send via
        // _pending_header_secrets / keep list so Redis never stores them.
        const publicHeaders: Record<string, string> = {};
        const pendingFromHeaders: Record<string, string> = { ...headerSecrets };
        for (const [name, value] of Object.entries(data.headers || {})) {
            if (isSensitiveHeader(name, String(value ?? ''))) {
                if (typeof value === 'string' && value.length > 0) {
                    pendingFromHeaders[name] = value;
                }
                continue;
            }
            publicHeaders[name] = value as string;
        }

        const basePayload: any = {
            organizationId: data.organizationId,
            subOrganizationId: data.subOrganizationId,
            name: data.name,
            url: data.url,
            method: data.method,
            headers: publicHeaders,
            paramsDef: data.paramsDef,
            input_schema: data.input_schema,
            output_schema: data.output_schema,
            timeout: data.timeout,
            auth: { type: 'none' },
            secret_headers_configured: keepSecretHeaderNames,
            _pending_header_secrets: pendingFromHeaders,
        };

        if (!isUpdate) {
            const created = await this.createNewApi(basePayload);
            apiId = created.data._id;
        }

        if (!apiId) {
            throw new Error('API id missing after create');
        }

        const newAuth: Record<string, unknown> = { type: authType };

        if (authType === 'bearer') {
            const token = secrets.token?.trim();
            if (token) {
                const { ref } = await this.writeApiSecret(apiId, {
                    organizationId: data.organizationId,
                    field: 'token',
                    value: token,
                });
                newAuth.token_ref = ref;
            } else if (isUpdate && data.auth?.token_ref) {
                newAuth.token_ref = data.auth.token_ref;
            }
        } else if (authType === 'basic') {
            newAuth.username = data.auth?.username;
            // Mirror bearer: only write when the user supplied a password.
            // Previously create always wrote (including "") and edit used
            // `typeof password === 'string'`, which could set password_ref
            // without a real Vault leaf — or skip writing after auth-type switch.
            const password = typeof secrets.password === 'string' ? secrets.password : '';
            if (password.length > 0) {
                const { ref } = await this.writeApiSecret(apiId, {
                    organizationId: data.organizationId,
                    field: 'password',
                    value: password,
                });
                newAuth.password_ref = ref;
            } else if (isUpdate && data.auth?.password_ref) {
                newAuth.password_ref = data.auth.password_ref;
            }
        } else if (authType === 'apikey') {
            newAuth.key = data.auth?.key;
            const value = secrets.value?.trim();
            if (value) {
                const { ref } = await this.writeApiSecret(apiId, {
                    organizationId: data.organizationId,
                    field: 'value',
                    value,
                });
                newAuth.value_ref = ref;
            } else if (isUpdate && data.auth?.value_ref) {
                newAuth.value_ref = data.auth.value_ref;
            }
        }

        return this.updateApi({
            ...basePayload,
            apiId,
            auth: newAuth,
        } as UpdateApiRequest);
    }
}

const apiManagerService = new ApiManagerService();
export { apiManagerService };
